import React, { ReactNode, useState } from "react";

import { Avatar, AvatarFallback } from "@/uiComponents/components/ui/avatar";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { Button } from "@/uiComponents/components/ui/button";
import PasswordChange from "./PasswordChange";
import { getDisplayRole } from "@/utils/roleHelper";
import { Check, Pen } from "lucide-react";
import { updateUserApi } from "@/modules/authenticationService";
import { updateDetails } from "@/store/redux/userDetailsSlice";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";

const Profile = () => {
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );

  const [isPasswordModalOpen, setIsPasswordModalOpen] =
    useState<boolean>(false);

  if (!userDetails) {
    return null;
  }
  return (
    <div className="ml-5 p-6 bg-gray-50 rounded-lg max-w-3xl dark:bg-gray-800">
      <div className="flex  ">
        <Avatar className="w-20 h-20 mb-4">
          <AvatarFallback className="text-3xl font-bold text-white bg-[#80BEA9]">
            {userDetails?.username?.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col ml-4 mt-3">
          {" "}
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
            {userDetails?.username}
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            {getDisplayRole(userDetails.role).label} at{" "}
            {userDetails?.organizationName}
          </p>
        </div>
      </div>

      <div className="pt-4 border-t-2">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-1">
          Account Overview
        </h3>
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 ">
            <DetailCard
              title="FirstName"
              value={userDetails?.firstName}
              editIcon={<Pen size={16} />}
            />
            <DetailCard
              title="LastName"
              value={userDetails?.lastName}
              editIcon={<Pen size={16} />}
            />
            <DetailCard title="Username" value={userDetails?.username} />
            <DetailCard title="Email" value={userDetails?.email} />
            <DetailCard
              title="Organization"
              value={userDetails?.organizationName}
            />
            <DetailCard
              title="Role"
              value={getDisplayRole(userDetails.role).label}
            />
            <DetailCard title="Branch" value={userDetails?.branchName} />
          </div>
        </div>

        <div className="pt-4 ml-0 border-t-2">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
            Account Settings
          </h3>
          <div className="flex justify-between items-center pl-6">
            <Button
              onClick={() => setIsPasswordModalOpen(true)}
              variant="outline"
              className="bg-[#191F29]  hover:bg-[#2A313D] active:bg-[#001533] text-white cursor-pointer hover:text-white"
            >
              Change Password
            </Button>
          </div>
        </div>
      </div>

      <PasswordChange
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </div>
  );
};

function DetailCard({
  title,
  value,
  editIcon,
}: {
  title: string;
  value?: string;
  editIcon?: ReactNode;
}) {
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value || "");

  const handleIconClick = () => {
    setIsEditing(true);
  };

  const handleConfirm = async () => {
    const params: { [key: string]: string } = {};

    if (title === "FirstName") {
      params.firstName = editedValue;
    }
    if (title === "LastName") {
      params.lastName = editedValue;
    }

    if (userDetails?.id != undefined) {
      try {
        await updateUserApi(userDetails?.id, params);

        if (userDetails) {
          const updateItems = {
            id: userDetails?.id,
            firstName: params.firstName || userDetails.firstName,
            lastName: params.lastName || userDetails.lastName,
            email: userDetails?.email,
            username: userDetails?.username,
            role: userDetails?.role,
            branch: userDetails.branch,
            branchName: userDetails.branchName,
            organizationId: userDetails.organizationId,
            organizationName: userDetails.organizationName,
          };

          dispatch(updateDetails(updateItems));
        }
      } catch (error) {
        const actualError = ErrorHelper(error);
        const message = Array.isArray(actualError)
          ? actualError[0]
          : actualError;
        toastMessage("error", message);
      }
    }
    setIsEditing(false);
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-700 p-3 pl-6 rounded-lg">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
        {title}
      </h3>
      <div className="flex justify-between items-center">
        {isEditing ? (
          <div className="relative w-full">
            <input
              className="  text-lg font-semibold text-gray-800 dark:text-white bg-transparent border-b border-gray-400 focus:outline-none focus:border-blue-500"
              value={editedValue}
              autoFocus
              onChange={(e) => setEditedValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleConfirm();
                }
              }}
            />
            <Check
              size={16}
              onClick={handleConfirm}
              className="absolute right-15 cursor-pointer top-1"
            />
          </div>
        ) : (
          <p className="text-lg font-semibold text-gray-800 dark:text-white">
            {value || "Not provided"}
          </p>
        )}
        {editIcon && !isEditing && (
          <span onClick={handleIconClick} className="cursor-pointer mt-1 mr-15">
            {editIcon}
          </span>
        )}
      </div>
    </div>
  );
}

export default Profile;
